'use strict'
import React, { useMemo, useState } from 'react'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip
} from '@material-ui/core'

const useStyles = makeStyles(theme => ({
  container: {
    maxHeight: 400,
    overflow: 'auto'
  },
  reassigned: {
    color: '#4caf50'
  },
  unassignedCell: {
    color: '#f44336',
    fontWeight: 600
  },
  warningChip: {
    height: 20,
    fontSize: '0.7rem'
  }
}))

const COLUMNS = [
  { id: 'propertyName', label: 'Property' },
  { id: 'fromCrewName', label: 'Current Crew' },
  { id: 'result', label: 'Result' },
  { id: 'reason', label: 'Reason' },
  { id: 'distanceMiles', label: 'Distance (mi)' },
  { id: 'warnings', label: 'Warning' }
]

export default function CutCrewPreviewTable ({ suggestedMoves, unassigned }) {
  const classes = useStyles()
  const [orderBy, setOrderBy] = useState('result')
  const [order, setOrder] = useState('asc')

  const rows = useMemo(() => {
    const moveRows = (suggestedMoves || []).map(m => ({
      ...m,
      result: m.toCrewName || 'Reassigned',
      resultType: 'reassigned'
    }))
    const unassignedRows = (unassigned || []).map(u => ({
      ...u,
      result: 'Unassigned',
      resultType: 'unassigned',
      distanceMiles: null,
      score: null
    }))
    return [...unassignedRows, ...moveRows]
  }, [suggestedMoves, unassigned])

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (orderBy === 'result') {
        const aVal = a.resultType === 'unassigned' ? 0 : 1
        const bVal = b.resultType === 'unassigned' ? 0 : 1
        return order === 'asc' ? aVal - bVal : bVal - aVal
      }
      const aVal = a[orderBy] ?? ''
      const bVal = b[orderBy] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal
      }
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [rows, orderBy, order])

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  return (
    <TableContainer className={classes.container}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map(col => (
              <TableCell key={col.id}>
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : 'asc'}
                  onClick={() => handleSort(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, idx) => (
            <TableRow key={row.aspirePropertyId || idx}>
              <TableCell>{row.propertyName}</TableCell>
              <TableCell>{row.fromCrewName}</TableCell>
              <TableCell className={row.resultType === 'reassigned' ? classes.reassigned : classes.unassignedCell}>
                {row.result}
              </TableCell>
              <TableCell>{(row.reason || '').replace(/_/g, ' ')}</TableCell>
              <TableCell>{row.distanceMiles != null ? row.distanceMiles : '—'}</TableCell>
              <TableCell>
                {(row.warnings || []).map((w, i) => (
                  <Chip key={i} label={w} size="small" className={classes.warningChip} color="secondary" variant="outlined" />
                ))}
              </TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">No displaced properties</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
